import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  workOrders as api, clients as clientsApi,
  type WorkOrder, type WorkOrderFilter, type WorkOrderMeta, type WorkOrderSummary,
} from '@/api/endpoints';
import type { Client } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Pagination, usePaginated } from '@/components/Pagination';
import { useSortable, SortHeader } from '@/components/SortableTable';
import { ZlecenieFormModal } from '@/components/ZlecenieFormModal';
import { formatPlnWhole } from '@/lib/money';

type SortKey = 'number' | 'client' | 'title' | 'orderDate' | 'deadline' | 'works' | 'amount';

/**
 * Rejestr zleceń na roboty (V156).
 *
 * Zlecenie NIE ma własnego statusu — kolumna „Stan" pokazuje stan wyliczony z jego prac, tą samą
 * regułą i tą samą paletą co mapa prac. Zlecenie bez żadnej pracy jest tu wyróżnione osobno:
 * to nie brak danych, tylko sygnał, że ktoś przyjął robotę i nikt jej nie rozpisał.
 */
export function ZleceniaPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'FIELDWORK_ORDER_EDIT');
  const canSeePricing = hasPermission(user, 'PRICING_VIEW');

  const [rows, setRows] = useState<WorkOrder[] | null>(null);
  const [summary, setSummary] = useState<WorkOrderSummary | null>(null);
  const [meta, setMeta] = useState<WorkOrderMeta | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [clientId, setClientId] = useState(params.get('clientId') ?? '');
  const [showCancelled, setShowCancelled] = useState(false);
  const [onlyWithoutWorks, setOnlyWithoutWorks] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WorkOrder | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  const filter: WorkOrderFilter = useMemo(() => ({
    search: debounced || undefined,
    clientId: clientId ? Number(clientId) : undefined,
    // Domyślnie rejestr pokazuje zlecenia żywe. Anulowane trzeba świadomie włączyć —
    // inaczej lista puchnie o rzeczy, których nikt już nie robi.
    cancelled: showCancelled ? undefined : false,
    withoutWorks: onlyWithoutWorks ? true : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [debounced, clientId, showCancelled, onlyWithoutWorks, dateFrom, dateTo]);

  const reload = () => {
    setErr(null);
    api.list(filter).then(setRows).catch(e => setErr(e?.message ?? String(e)));
    api.summary(filter).then(setSummary).catch(() => {});
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);
  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    clientsApi.list().then(setClients).catch(() => {});
  }, []);

  const { sorted, sort, onHeaderClick } = useSortable<WorkOrder, SortKey>(
    rows ?? [], 'orderDate', 'desc',
    (r, key) => {
      switch (key) {
        case 'number': return r.number;
        case 'client': return r.clientName;
        case 'title': return r.title;
        case 'orderDate': return r.orderDate;
        case 'deadline': return r.deadline;
        case 'works': return r.worksTotal;
        case 'amount': return r.netAmount;
        default: return null;
      }
    });

  const { pagedItems, page, pageSize, setPage, setPageSize, total } =
    usePaginated(sorted, { storageKey: 'work-orders' });

  return (
    <div className="max-w-[1500px]">
      <header className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1>Zlecenia na roboty</h1>
          <div className="text-ink-400 text-[13px]">
            Podstawa, na której brygada jedzie w teren — także zlecenia przyjęte telefonicznie, bez oferty
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav('/prace')} className="btn btn-secondary btn-sm">
            <Icon name="layout" className="w-3.5 h-3.5"/> Rejestr prac
          </button>
          {canEdit && (
            <button onClick={() => { setEditing(null); setModalOpen(true); }} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowe zlecenie
            </button>
          )}
        </div>
      </header>

      {summary && (
        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: `repeat(${canSeePricing ? 5 : 4}, minmax(0,1fr))` }}>
          <Kpi label="Zlecenia" value={summary.total}/>
          <Kpi label="Aktywne" value={summary.active}/>
          {/* Najważniejszy licznik tego ekranu: przyjęte, a nikt nie rozpisał prac. */}
          <Kpi label="Bez rozpisanych prac" value={summary.withoutWorks}
               tone={summary.withoutWorks > 0 ? 'amber' : undefined}
               hint={summary.withoutWorks > 0 ? 'nikt nie założył pracy' : undefined}/>
          <Kpi label="Po terminie" value={summary.overdue} tone={summary.overdue > 0 ? 'red' : undefined}/>
          {canSeePricing && <Kpi label="Suma netto" value={formatPlnWhole(summary.netTotal)}/>}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-head flex-wrap gap-2">
          <div className="flex items-center gap-2 bg-ink-50 border border-ink-100 px-2.5 py-1.5 rounded-md text-ink-400 flex-1 min-w-[240px] max-w-[400px]">
            <Icon name="search"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Szukaj: numer, klient, opis, numer u klienta, oferta…"
                   className="flex-1 border-0 bg-transparent outline-none text-[13px] text-ink-800"/>
          </div>
          <div className="w-[220px]">
            <SearchableSelect value={clientId} onChange={setClientId} clearable placeholder="Klient — wszyscy">
              {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </SearchableSelect>
          </div>
          <label className="flex items-center gap-1.5 text-[12px]">
            <span className="text-ink-500">Od</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                   className="input !py-1 !text-[12px] w-[135px]"/>
          </label>
          <label className="flex items-center gap-1.5 text-[12px]">
            <span className="text-ink-500">do</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                   className="input !py-1 !text-[12px] w-[135px]"/>
          </label>
          <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
            <input type="checkbox" checked={onlyWithoutWorks} onChange={e => setOnlyWithoutWorks(e.target.checked)}/>
            <span>Bez rozpisanych prac</span>
          </label>
          <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
            <input type="checkbox" checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)}/>
            <span>Pokaż anulowane</span>
          </label>
        </div>

        <div className="card-body !p-0 overflow-x-auto">
          {err && <div className="text-red-600 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">
              Brak zleceń. Zarejestruj pierwsze — do zlecenia z telefonu wystarczą dwa pola.
            </div>
          )}
          {rows && rows.length > 0 && (
            <>
              <table className="table-hw">
                <thead>
                  <tr>
                    <SortHeader label="Numer" sortKey="number" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Klient" sortKey="client" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Czego dotyczy" sortKey="title" current={sort} onClick={onHeaderClick}/>
                    <th>Wpływ</th>
                    <SortHeader label="Data" sortKey="orderDate" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Termin" sortKey="deadline" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Prace" sortKey="works" current={sort} onClick={onHeaderClick}/>
                    <th>Stan</th>
                    {canSeePricing && <SortHeader label="Netto" sortKey="amount" current={sort} onClick={onHeaderClick} align="right"/>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map(o => (
                    <tr key={o.id} className={'hover:bg-ink-50 cursor-pointer ' + (o.cancelled ? 'opacity-60' : '')}
                        onClick={() => nav(`/zlecenia/${o.id}`)}>
                      <td className="font-mono text-[12px] text-water-600 whitespace-nowrap">
                        {o.number}
                        {o.clientOrderRef && (
                          <div className="text-[11px] text-ink-400" title="Numer u klienta">{o.clientOrderRef}</div>
                        )}
                      </td>
                      <td className="text-[13px]">
                        <div className="text-ink-900 truncate max-w-[200px]">{o.clientName ?? '—'}</div>
                        {o.clientCity && <div className="text-[11px] text-ink-400">{o.clientCity}</div>}
                      </td>
                      <td className="text-[13px] text-ink-800 truncate max-w-[300px]">
                        {o.title}
                        {(o.sourceOfferNumber || o.sourceOfferRef) && (
                          <div className="text-[11px] text-ink-400">z oferty {o.sourceOfferNumber ?? o.sourceOfferRef}</div>
                        )}
                      </td>
                      <td className="text-[12px] text-ink-600 whitespace-nowrap">{o.receivedViaName}</td>
                      <td className="font-mono text-[12px] text-ink-600">{o.orderDate}</td>
                      <td className="text-[12px] whitespace-nowrap">
                        {o.deadline ? (
                          <>
                            <span className="font-mono text-ink-700">{o.deadline}</span>
                            {o.daysToDeadline != null && (
                              <div className={'text-[11px] ' + (o.daysToDeadline < 0 ? 'text-red-600 font-semibold'
                                : o.daysToDeadline <= 7 ? 'text-amber-600' : 'text-ink-400')}>
                                {o.daysToDeadline < 0 ? `po terminie ${-o.daysToDeadline} dni` : `za ${o.daysToDeadline} dni`}
                              </div>
                            )}
                          </>
                        ) : <span className="text-ink-300">—</span>}
                      </td>
                      <td className="text-[12px] whitespace-nowrap">
                        {o.worksTotal === 0
                          ? <span className="text-amber-700">brak</span>
                          : <>
                              <strong>{o.worksTotal}</strong>
                              {o.worksOverdue > 0 && (
                                <span className="text-red-600 ml-1" title="prace po terminie umowy">
                                  ({o.worksOverdue} po terminie)
                                </span>
                              )}
                            </>}
                      </td>
                      <td className="text-[12px] whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: o.statusColor }}/>
                          {o.cancelled ? 'Anulowane' : o.derivedStatusName}
                        </span>
                      </td>
                      {canSeePricing && (
                        <td className="font-mono text-[12px] text-ink-800 text-right whitespace-nowrap">
                          {formatPlnWhole(o.netAmount)}
                          {!!o.priceVariants?.length && (
                            <div className="font-sans text-[11px] text-ink-400"
                                 title={o.priceVariants.map(v => `${v.billed ? '✓' : '–'} ${v.label}: ${formatPlnWhole(v.netAmount)}`).join('\n')}>
                              {o.priceVariants.filter(v => v.billed).length} z {o.priceVariants.length} wariantów
                            </div>
                          )}
                        </td>
                      )}
                      <td className="text-right" onClick={e => e.stopPropagation()}>
                        {canEdit && (
                          <button onClick={() => { setEditing(o); setModalOpen(true); }}
                                  className="btn btn-secondary btn-sm" title="Edytuj">
                            <Icon name="edit" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                        <Icon name="chevron" className="text-ink-300 inline ml-2"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 border-t border-ink-100">
                <Pagination total={total} page={page} pageSize={pageSize}
                            onPageChange={setPage} onPageSizeChange={setPageSize}/>
              </div>
            </>
          )}
        </div>
      </div>

      <ZlecenieFormModal open={modalOpen} initial={editing} clients={clients} meta={meta}
                         onClose={() => setModalOpen(false)}
                         onSaved={() => { setModalOpen(false); reload(); }}/>
    </div>
  );
}

function Kpi({ label, value, hint, tone }: {
  label: string; value: number | string; hint?: string; tone?: 'amber' | 'red';
}) {
  const color = tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-600' : 'text-ink-800';
  return (
    <div className="card">
      <div className="card-body !py-3">
        <div className={'text-[24px] font-bold leading-none ' + color}>{value}</div>
        <div className="text-[11px] text-ink-500 uppercase tracking-wider mt-1">{label}</div>
        {hint && <div className="text-[11px] text-ink-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
